import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { loadReCaptcha } from 'react-recaptcha-v3';

import omit from 'lodash/omit';
import { gettext } from '@eb/i18n';
import { isAnEmail } from '@eb/validators';

import { withMainControls } from '@eb/eds-structure';

import { AlertChunky } from '@eb/eds-iconography';
import { ICON_TYPE_PROP_TYPE } from '@eb/eds-icon';
import { TYPE_ERROR } from '@eb/eds-notification';
import { Button } from '@eb/eds-button';

import PageHeader from '../../components/PageHeader';
import SignupEmailForm from '../../components/newForms/SignupEmailForm';
import SignupForm from '../../components/newForms/SignupForm';
import SplitContainer from '../../connectedComponents/SplitContainer';
import ConnectedSocialLoginOptions from '../../connectedComponents/ConnectedSocialLoginOptions';
import SoftAccountSection from '../../connectedComponents/SoftAccountSection';

import { SIGN_UP_FORM } from '../../constants';
import {
    TOS_CREATE_ACCOUNT_MUST_BE_ACCEPTED,
    TOS_SOCIAL_MUST_BE_ACCEPTED,
} from '../../constants/messages';
import {
    TOSMODAL_NEWACCOUNT_SHOWED,
    TOSMODAL_NEWACCOUNT_DENIED,
    TOSMODAL_NEWACCOUNT_ACCEPTED,
    TOSMODAL_SOCIAL_SHOWED,
    TOSMODAL_SOCIAL_DENIED,
} from '../../constants/analytics';

import ToSModal from '../../connectedComponents/ToSModal';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/SIGN_UP_PATH
 */
class SignupPage extends React.PureComponent {
    static propTypes = {
        onSignUpUser: PropTypes.func,
        email: PropTypes.string,
        locale: PropTypes.string,
        onLoginInsteadClick: PropTypes.func,
        trackEmailConfirmationEdit: PropTypes.func,
        trackEmailMismatchShowed: PropTypes.func,
        clearAuthNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        fp: PropTypes.bool,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
        }),
        authNotificationOptions: PropTypes.shape({
            message: PropTypes.node,
            type: PropTypes.string,
            iconType: ICON_TYPE_PROP_TYPE,
        }),
        launchAsyncStrategyForAuthSocialScripts: PropTypes.bool,
    };

    componentDidMount() {
        const {
            recaptcha3PublicKey,
            disableRecaptcha3,
            cleanSocialAuthenticationData,
            clearAuthNotification,
            loadGoogleOneTap,
            launchGoogleOneTapWeb,
        } = this.props;
        if (!disableRecaptcha3) {
            loadReCaptcha(recaptcha3PublicKey);
        }
        //Clear notification and social data when page is loaded.
        clearAuthNotification();
        cleanSocialAuthenticationData();
        if (launchGoogleOneTapWeb) {
            //Load google one tap
            loadGoogleOneTap?.();
        }
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification, onSignupLookup } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onSignupLookup({ ...formData, isSignup: true });
    };

    _handleToSAccepted = () => {
        const {
            onSignUpUser,
            acceptToS,
            acceptSocialToS,
            signUpSecondStep,
        } = this.props;

        if (signUpSecondStep) {
            acceptToS({
                onAccept: onSignUpUser,
                formName: SIGN_UP_FORM,
                actionName: TOSMODAL_NEWACCOUNT_ACCEPTED,
            });
        } else {
            acceptSocialToS();
        }
    };
    _handleToSDisplay = () => {
        const { showToS, signUpSecondStep } = this.props;

        showToS({
            actionName: signUpSecondStep
                ? TOSMODAL_NEWACCOUNT_SHOWED
                : TOSMODAL_SOCIAL_SHOWED,
        });
    };
    _handleToSDenied = () => {
        const { cancelToS, signUpSecondStep } = this.props;

        cancelToS({
            actionName: signUpSecondStep
                ? TOSMODAL_NEWACCOUNT_DENIED
                : TOSMODAL_SOCIAL_DENIED,
            notificationMessage: signUpSecondStep
                ? TOS_CREATE_ACCOUNT_MUST_BE_ACCEPTED
                : TOS_SOCIAL_MUST_BE_ACCEPTED,
        });
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    };

    _handleGoToLogin = () => {
        const {
            setSignupSecondStep,
            clearAuthNotification,
            onLoginInsteadClick,
        } = this.props;
        if (clearAuthNotification) {
            clearAuthNotification();
        }
        if (setSignupSecondStep) {
            setSignupSecondStep(false);
        }
        if (onLoginInsteadClick) {
            onLoginInsteadClick();
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    _handleSoftAccount = () => {
        const {
            handleSoftAccount,
            clearAuthNotification,
            resetForm,
        } = this.props;
        clearAuthNotification();
        handleSoftAccount({});
        if (resetForm) {
            resetForm(SIGN_UP_FORM);
        }
    };
    render() {
        const {
            form,
            email,
            locale,
            recaptcha2PublicKey,
            showCaptcha,
            fp,
            socialSSOSettings,
            authNotificationOptions,
            clearAuthNotification,
            signUpSecondStep,
            trackEmailConfirmationEdit,
            trackEmailMismatchShowed,
            previousFormEmail,
            isSoftAccount,
            launchAsyncStrategyForAuthSocialScripts,
            location,
        } = this.props;

        const emailParams = location?.query?.email;
        let initialEmail = previousFormEmail || '';
        if (!previousFormEmail && emailParams) {
            // If the email is in the URL params, set it in the form
            initialEmail = emailParams;
        }
        let syncErrors;
        let softAccountModal;

        if (form[SIGN_UP_FORM]) {
            syncErrors = form[SIGN_UP_FORM].syncErrors;
        }

        if (isSoftAccount) {
            softAccountModal = (
                <SoftAccountSection
                    handleSoftAccount={this._handleSoftAccount}
                />
            );
        }

        let pageHeaderProps = {
            headerText: gettext('Create an account'),
            subtitleText: null,
            usesNewStyles: true,
        };
        const buttonCTA = gettext('Continue');
        const buttonCTACreate = gettext('Create account');
        const optionalSocialProps = launchAsyncStrategyForAuthSocialScripts
            ? { asyncStrategy: 'async' }
            : {};

        const formProps = {
            formErrors: syncErrors,
            onLoginInsteadClick: this._trackLoginInsteadClick,
            clearAuthNotification: clearAuthNotification,
            locale: locale,
            recaptcha2PublicKey: recaptcha2PublicKey,
            showCaptcha: showCaptcha,
            email: email,
            fp: fp,
            showNotification: this._handleShowNotification,
            clearNotification: this._handleClearNotification,
            socialSSOSettings: socialSSOSettings,
        };

        let signupScreen = (
            <div>
                <SignupEmailForm
                    {...formProps}
                    buttonCTA={buttonCTA}
                    onSubmit={this._handleSubmit}
                    initialValues={{ email: initialEmail }}
                />
                <div className="split-container__divider eds-l-pad-bot-5 eds-l-pad-top-5">
                    <div className="split-container__divider-text eds-text-bm eds-text-weight--heavy">
                        {gettext('or')}
                    </div>
                </div>
                <div data-testid="social-login-section">
                    <ConnectedSocialLoginOptions
                        buttonAppleText={gettext('Sign up with Apple')}
                        buttonFBText={gettext('Sign up with Facebook')}
                        buttonGoogleText={gettext('Sign up with Google')}
                        {...optionalSocialProps}
                    />
                </div>
            </div>
        );

        if (signUpSecondStep && email && isAnEmail(email)) {
            // Then show the second screen
            signupScreen = (
                <SignupForm
                    {...formProps}
                    buttonCTA={buttonCTACreate}
                    onSubmit={this._handleToSDisplay}
                    trackEmailConfirmationEdit={trackEmailConfirmationEdit}
                    trackEmailMismatchShowed={trackEmailMismatchShowed}
                    handleEmailEdit={this._handleGoToLogin}
                />
            );
        }

        // hide subtitle when an auth notification is being displayed
        if (!isEmpty(authNotificationOptions)) {
            pageHeaderProps = omit(pageHeaderProps, 'subtitleText');
        }

        return (
            <SplitContainer
                shouldShowSecondaryPanel={true}
                usesNewStyles={true}
                header={<PageHeader {...pageHeaderProps} />}
            >
                {signupScreen}

                <ToSModal
                    dataTestId="signup-tos"
                    onAcceptToS={this._handleToSAccepted}
                    onCancelToS={this._handleToSDenied}
                />
                {softAccountModal}
                <div className="eds-text-color--ui-800 eds-text-weight--heavy eds-l-pad-bot-5 eds-l-pad-top-5 eds-l-pad-left-5">
                    <Button
                        onClick={this._handleGoToLogin}
                        type="button"
                        style="anchor"
                        data-testid="login-instead"
                    >
                        {gettext('Log In')}
                    </Button>
                </div>
            </SplitContainer>
        );
    }
}

export default withMainControls(SignupPage);
